import React from 'react';
import { ALERT_TITLE, ALERT_TYPE, CHIP_VARIANT } from '@constants/alertsConstant';
import { PAGE, ROLE_CONST, SOURCE } from '@constants/commonConstants';
import {
  getForecastSubmittedStatus,
  getPlResubmissionStatus,
  getUserRole,
} from '@services/commonServices';
import {
  dateFormatWithoutYear,
  formatDateToYYYYMMDD,
  getRemainingDays,
  getTime12Hr,
} from '@util/commonUtil';
import { appInsights } from '@services/appInsightsService';
import { getOpportunitiesDetails } from '@services/pursuitLeaderServices';

export const QuaterlyDetailsChangeKeys = OppArray => {
  return OppArray.map(item => {
    return {
      commit: item.commit_status,
      opportunity_id: item.opportunity_id,
      tov: item.tov,
      awd: formatDateToYYYYMMDD(item.awd),
      month: item.month,
      period: item.awd_period,
      fiscal_year: item.fiscal_year,
      status: item.statuscode,
      stage: item.stagecode,
      assurance: item.ASU, //ASU
      consulting: item.CNS, //CNS
      sat: item.SAT,
      tax: item.TAX,
      pas: item.PAS,
      itts: item.ITTS,
      emia_acr: item.EMIA_ACR, //EMIA_ACR
      account: item?.account,
      close_probability: item.close_probablity,
      ey_id: item.ey_id ? item.ey_id : '0000',
      opportunity_name: item?.opportunity_name,
      client_id: item.client,
      client_name: item.client_name,
      stage_name: item.stagecode_name,
      account_name: item?.account_name,
    };
  });
};
export const MslOppDetails = mslOpp => {
  return mslOpp.map(item => {
    return {
      account_segment: item.account_segment,
      accounts: item.accounts,
      tov: item.tov,
      awd: formatDateToYYYYMMDD(item.awd),
      month: item.month,
      period: item.awd_period,
      fiscal_year: item.fiscal_year,
      status: item.statuscode,
      stage: item.stagecode,
      assurance: item.ASU, //ASU
      consulting: item.CNS, //CNS
      sat: item.SAT,
      tax: item.TAX,
      pas: item.PAS,
      itts: item.ITTS,
      emia_acr: item.EMIA_ACR, //EMIA_ACR
      account: item?.account,
      close_probability: item.close_probablity,
      ey_id: item.ey_id ? item.ey_id : '0000',
      opportunity_name: item?.opportunity_name,
      client_id: item.client,
      client_name: item.client_name,
      stage_name: item.stagecode_name,
      account_name: item?.account_name,
    };
  });
};

export const filterOpportunityById = (array1, array2) => {
  const filteredArray = array2.filter(item2 =>
    array1.some(item1 => item1.opp_id === item2.opportunity_id),
  );
  return filteredArray;
};

export const GcspPostChangeKeys = (OppArray, SubmissionType, isRollingData, MONTH) => {
  // Static month written for condition to send year + 1
  // if month is july or aug sending year + 1;
  const CurrentMonth = ['may', 'jun'];
  const PostMonth = ['jul', 'aug'];
  return OppArray?.map(item => {
    const data = {
      assurance: Number(item?.Assurance) || 0, //ASU Adding default value 0 if value is null
      consulting: Number(item?.consulting) || 0, //CNS
      sat: Number(item?.sat) || 0,
      tax: Number(item?.tax) || 0,
      pas: Number(item?.pas) || 0,
      itts: Number(item?.itts) || 0,
      emia_acr: Number(item?.EMIAACR) || 0,
      submission_type: SubmissionType,
    };

    if (isRollingData) {
      if (
        CurrentMonth.includes(MONTH.toLowerCase()) &&
        PostMonth.includes(item.month.toLowerCase())
      ) {
        data.month = item.month;
        data.quarter = item.quarter;
        data.year = item.year + 1;
      } else {
        data.month = item.month;
        data.quarter = item.quarter;
        data.year = item.year;
      }
    }
    return data;
  });
};

export const MslPostChangeKeys = (OppArray, SubmissionType, isRollingData) => {
  return OppArray?.map(item => {
    const data = {
      assurance: Number(item?.assurance) || 0, //ASU
      consulting: Number(item?.consulting) || 0, //CNS
      sat: Number(item?.sat) || 0,
      tax: Number(item?.tax) || 0,
      itts: Number(item?.itts) || 0,
      emeia_acr: Number(item?.emeia) || 0,
      submission_type: SubmissionType,
    };

    if (isRollingData) {
      data.month = item.month;
      data.quarter = item.quarter;
      data.year = item.year;
    }
    return data;
  });
};

export const getCurrentUser = data => {
  let userRole = data?.role;

  // Map read-only roles to their base roles for routing purposes
  if (userRole === ROLE_CONST.GCSP_READ_ONLY) {
    userRole = ROLE_CONST.GCSP;
  } else if (userRole === ROLE_CONST.MSL_READ_ONLY) {
    userRole = ROLE_CONST.MSL;
  } else if (userRole === ROLE_CONST.MSL_DELEGATE) {
    userRole = ROLE_CONST.MSL;
  }

  // Sort accountList alphabetically by name to ensure the first account_id
  // matches the sorted dropdown list (which sorts by accountName including role prefix,
  // but since all accounts in the list have the same role prefix, sorting by name produces the same order)
  let sortedAccountList = data?.accountList || [];
  if (sortedAccountList.length > 1) {
    sortedAccountList = [...sortedAccountList].sort((a, b) => {
      // Check if account names start with digits
      const isFirstAccountNumber = /^\d/.test(a.name);
      const isSecondAccountNumber = /^\d/.test(b.name);

      // Sort numeric names numerically (extract leading digits)
      if (isFirstAccountNumber && isSecondAccountNumber) {
        return parseInt(a.name, 10) - parseInt(b.name, 10);
      } else if (isFirstAccountNumber) {
        // Numeric names come before alphabetic names
        return -1;
      } else if (isSecondAccountNumber) {
        return 1;
      } else {
        // Sort alphabetically
        return a.name.localeCompare(b.name);
      }
    });
  }

  const user = {
    email: localStorage.getItem('userEmail'),
    role: userRole,
    account_id: sortedAccountList[0]?.account_id,
  };

  return user;
};

export const getOldCurrentUser = data => {
  let userRole = data?.role;

  // Map read-only roles to their base roles for routing purposes
  if (userRole === ROLE_CONST.GCSP_READ_ONLY || userRole === 'GCSP READ ONLY') {
    userRole = ROLE_CONST.GCSP;
  } else if (userRole === ROLE_CONST.MSL_READ_ONLY || userRole === 'MSL READ ONLY') {
    userRole = ROLE_CONST.MSL;
  } else if (userRole === ROLE_CONST.MSL_DELEGATE || userRole === 'MSL DELEGATE') {
    userRole = ROLE_CONST.MSL;
  }

  const user = {
    email: localStorage.getItem('userEmail'),
    role: userRole,
    account_id: data?.account_id,
    apiRole: data?.apiRole,
  };

  return user;
};

export const filterParams = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    var dateAsString = cellValue;
    if (dateAsString === null) return -1;
    var dateParts = dateAsString.split('/');
    var cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0;
    }
    return cellDate < filterLocalDateAtMidnight ? -1 : cellDate > filterLocalDateAtMidnight ? 1 : 0;
  },
};

export const DateFiltering = {
  // For YYYY-MM-DD Date Formate
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    var dateAsString = cellValue;
    if (dateAsString === null) return -1;
    var dateParts = dateAsString.split('-');
    var cellDate = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0;
    }
    return cellDate < filterLocalDateAtMidnight ? -1 : cellDate > filterLocalDateAtMidnight ? 1 : 0;
  },
};

export const DateSorting = (date1, date2) => {
  // For YYYY-MM-DD Date Formate
  const monthToComparableNumber = date => {
    if (date === undefined || date === null) {
      return null;
    }
    const parts = date.split('-');
    const dayNumber = Number.parseInt(parts[2]);
    const monthNumber = Number.parseInt(parts[1]) * 100;
    const yearNumber = Number.parseInt(parts[0]) * 10000;
    return yearNumber + monthNumber + dayNumber;
  };

  const date1Number = monthToComparableNumber(date1);
  const date2Number = monthToComparableNumber(date2);
  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  }
  if (date2Number === null) {
    return 1;
  }
  return date1Number - date2Number;
};

export const OppsNameSorting = (name1, name2) => {
  if (name1 === undefined || name2 === undefined) {
    return;
  }

  return name1.localeCompare(name2);
};

export const clientAccountNameSorting = (valueA, valueB) => {
  if (valueA === undefined || valueB === undefined) {
    return;
  }

  const isSpecialChar = char => /[^a-zA-Z0-9]/.test(char);
  const isNumericChar = char => /[0-9]/.test(char);
  const isAlphabeticChar = char => /[a-zA-Z]/.test(char);

  const getOrder = char => {
    if (isSpecialChar(char)) return 1;
    if (isNumericChar(char)) return 2;
    if (isAlphabeticChar(char)) return 3;
    return 4;
  };
  const trimmedA = valueA.trim();
  const trimmedB = valueB.trim();
  const orderA = getOrder(trimmedA.charAt(0));
  const orderB = getOrder(trimmedB.charAt(0));
  if (orderA !== orderB) {
    return orderA - orderB;
  }

  return trimmedA.localeCompare(trimmedB);
};

export const QuarterYearSorting = (a, b) => {
  const yearA = a.slice(-4);
  const yearB = b.slice(-4);
  const quarterA = a.slice(0, 2);
  const quarterB = b.slice(0, 2);

  // Compare years first
  if (yearA !== yearB) {
    return yearA - yearB;
  }

  // If years are equal, compare quarters
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  return quarters.indexOf(quarterA) - quarters.indexOf(quarterB);
};

// Revised Code 2
export const getAlertBarDetails = async (quarterData, apiPayload, screenName, systemDateTime) => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering getAlertBarDetails function with parameters ${JSON.stringify({
        quarterData,
        apiPayload,
        screenName,
        systemDateTime,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  // Added to elimanate chip for GCSP READ ONLY in userdropdown
  if (apiPayload.role === 'GCSP READ ONLY' || apiPayload.role === 'MSL READ ONLY') return {};

  // For read only user's alert
  if (apiPayload?.read_only) {
    return {
      description:
        'Based on your access rights, this content is currently displayed in read-only mode',
      title: ALERT_TITLE.READ_ONLY,
      type: ALERT_TYPE.INFO,
      showButton: false,
      chipTitle: 'Read-Only',
      chipType: 'chip',
      chipVariant: CHIP_VARIANT.MONOCHROME_DARK,
      modifyStyle: null,
    };
  }

  let alertDetails;

  const forcastOpensInDays = getRemainingDays(quarterData.persona_startdate, systemDateTime);
  const forcastDueInDays = getRemainingDays(quarterData.persona_closedate, systemDateTime);

  const personaStartDate = dateFormatWithoutYear(quarterData.persona_startdate);
  const personaCloseDate = dateFormatWithoutYear(quarterData.persona_closedate);
  const personaNextStartDate = dateFormatWithoutYear(quarterData.persona_nextstartdate);

  const startDateTime = getTime12Hr(quarterData.persona_startdate);
  const closeDateTime = getTime12Hr(quarterData.persona_closedate);
  const nextStartDateTime = getTime12Hr(quarterData.persona_nextstartdate);

  if (forcastOpensInDays > 5) {
    alertDetails = {
      description: `Next Forecast opens on ${personaStartDate}, ${startDateTime}`,
      title: ALERT_TITLE.FORECAST_OPENS_SOON,
      type: ALERT_TYPE.WARNING,
      showButton: false,
      chipTitle: `Opens on ${personaStartDate}, ${startDateTime}`,
      chipType: 'chip',
      chipVariant: CHIP_VARIANT.MONOCHROME_DARK,
      modifyStyle: null,
    };
  } else if (forcastOpensInDays <= 5 && forcastOpensInDays >= 2) {
    alertDetails = {
      description: `Forecast opens in ${forcastOpensInDays} days, ${personaStartDate}, ${startDateTime}`,
      title: ALERT_TITLE.FORECAST_OPENS_SOON,
      type: ALERT_TYPE.WARNING,
      showButton: false,
      chipTitle: `Opens on ${personaStartDate}, ${startDateTime}`,
      chipType: 'chip',
      chipVariant: CHIP_VARIANT.WARNING,
      modifyStyle: null,
    };
  } else if (forcastOpensInDays === 1) {
    alertDetails = {
      description: `Forecast opens tomorrow, ${personaStartDate}, ${startDateTime}`,
      title: ALERT_TITLE.FORECAST_OPENS_TOMORROW,
      type: ALERT_TYPE.WARNING,
      showButton: false,
      chipTitle: `Opens Tomorrow, ${startDateTime}`,
      chipType: 'chip',
      chipVariant: CHIP_VARIANT.WARNING,
      modifyStyle: null,
    };
  } else if (forcastOpensInDays <= 0) {
    if (new Date(systemDateTime) < new Date(quarterData.persona_startdate)) {
      alertDetails = {
        description: `Forecast opens today, ${personaStartDate}, ${startDateTime}`,
        title: ALERT_TITLE.FORECAST_OPENS_TODAY,
        type: ALERT_TYPE.WARNING,
        showButton: false,
        chipTitle: `Opens Today, ${startDateTime}`,
        chipType: 'chip',
        chipVariant: CHIP_VARIANT.WARNING,
        modifyStyle: null,
      };
    } else {
      let status;
      try {
        status = await getForecastSubmittedStatus(apiPayload);
      } catch (error) {
        status = {};
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Error from getForecastSubmittedStatus ${JSON.stringify({
              error,
            })}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents error log
        });
        logError('getForecastSubmittedStatus', error);
      }
      if (status?.monthly_submission === 'Submitted') {
        // Check if current time has passed persona_closedate (including time component)
        const hasPersonaWindowClosed =
          new Date(systemDateTime) > new Date(quarterData.persona_closedate);

        // For MSL and GCSP, if window closed, show "FORECAST OPENS SOON" instead of "FORECAST SUBMITTED"
        if (
          (apiPayload?.role === 'MSL' ||
            apiPayload?.role === 'GCSP' ||
            apiPayload?.role === 'PL') &&
          hasPersonaWindowClosed
        ) {
          alertDetails = {
            description: `Next Forecast opens on ${personaNextStartDate}, ${nextStartDateTime}`,
            title: ALERT_TITLE.FORECAST_OPENS_SOON,
            type: ALERT_TYPE.WARNING,
            showButton: false,
            chipTitle: `Opens on ${personaNextStartDate}, ${nextStartDateTime}`,
            chipType: 'chip',
            chipVariant: CHIP_VARIANT.WARNING,
            modifyStyle: null,
          };
        } else if (apiPayload?.role === 'PL') {
          const resubmitStatus = await getPlResubmissionStatus(apiPayload);
          if (resubmitStatus.resubmit_status === true) {
            alertDetails = {
              description: `Forecast has been submitted and can be edited until ${personaCloseDate}, ${closeDateTime}`,
              title: ALERT_TITLE.FORECAST_SUBMITTED,
              type: ALERT_TYPE.SUCCESS,
              showButton: true,
              chipTitle: 'Submitted',
              chipType: 'outlined',
              chipVariant: CHIP_VARIANT.SUCCESS,
              modifyStyle: null,
            };
          } else {
            alertDetails = {
              description: `Forecast has been submitted and can be edited until ${personaCloseDate}, ${closeDateTime}`,
              title: ALERT_TITLE.FORECAST_SUBMITTED,
              type: ALERT_TYPE.SUCCESS,
              showButton: true,
              chipTitle: 'Submitted',
              chipType: 'outlined',
              chipVariant: CHIP_VARIANT.SUCCESS,
              modifyStyle: null,
            };
          }
        } else {
          alertDetails = {
            description: `Forecast has been submitted and can be edited until ${personaCloseDate}, ${closeDateTime}`,
            title: ALERT_TITLE.FORECAST_SUBMITTED,
            type: ALERT_TYPE.SUCCESS,
            showButton: true,
            chipTitle: 'Submitted',
            chipType: 'outlined',
            chipVariant: CHIP_VARIANT.SUCCESS,
            modifyStyle: null,
          };
        }
      } else {
        if (forcastDueInDays > 5) {
          alertDetails = {
            description: `Forecast due on ${personaCloseDate}, ${closeDateTime}`,
            title: ALERT_TITLE.FORECAST_DUE,
            type: ALERT_TYPE.WARNING,
            showButton: true,
            chipTitle: `Due on ${personaCloseDate}, ${startDateTime}`,
            chipType: 'chip',
            chipVariant: CHIP_VARIANT.MONOCHROME_DARK,
            modifyStyle: null,
          };
        } else if (forcastDueInDays <= 5 && forcastDueInDays >= 2) {
          alertDetails = {
            description: `Forecast due in ${forcastDueInDays} days, on ${personaCloseDate}, ${closeDateTime}`,
            title: ALERT_TITLE.FORECAST_DUE_SOON,
            type: ALERT_TYPE.NEEDS_ACTION,
            showButton: true,
            chipTitle: `Due on ${personaCloseDate}, ${closeDateTime}`,
            chipType: 'chip',
            chipVariant: CHIP_VARIANT.NEEDS_ACTION,
            modifyStyle: null,
          };
        } else if (forcastDueInDays === 1) {
          alertDetails = {
            description: `Forecast due tomorrow, ${personaCloseDate}, ${closeDateTime}`,
            title: ALERT_TITLE.FORECAST_DUE_TOMORROW,
            type: ALERT_TYPE.NEEDS_ACTION,
            showButton: true,
            chipTitle: `Due Tomorrow, ${closeDateTime}`,
            chipType: 'chip',
            chipVariant: CHIP_VARIANT.NEEDS_ACTION,
            modifyStyle: null,
          };
        } else if (forcastDueInDays === 0) {
          if (new Date(systemDateTime) < new Date(quarterData.persona_closedate)) {
            alertDetails = {
              description: `Forecast due today, ${personaCloseDate}, ${closeDateTime}`,
              title: ALERT_TITLE.FORECAST_DUE_TODAY,
              type: ALERT_TYPE.NEEDS_ACTION,
              showButton: true,
              chipTitle: `Due Today, ${closeDateTime}`,
              chipType: 'chip',
              chipVariant: CHIP_VARIANT.NEEDS_ACTION,
              modifyStyle: null,
            };
          } else {
            alertDetails = {
              description: `Next Forecast opens on ${personaNextStartDate}, ${nextStartDateTime}`,
              title: ALERT_TITLE.FORECAST_OPENS_SOON,
              type: ALERT_TYPE.WARNING,
              showButton: false,
              chipTitle: `Opens on ${personaNextStartDate}, ${nextStartDateTime}`,
              chipType: 'chip',
              chipVariant: CHIP_VARIANT.WARNING,
              modifyStyle: null,
            };
          }
        }
      }
    }
  }

  if (alertDetails !== undefined && alertDetails !== null) {
    // If page is Forecast and account is used by Super User then removing Start Forecast button from alert
    if (
      screenName === PAGE.FORECAST ||
      (localStorage.getItem('isSuperUser') == 'true' &&
        localStorage.getItem('targetUserEmail') !== 'null')
    ) {
      alertDetails.showButton = false;

      if (apiPayload?.role === 'PL') {
        const oppsBadDataDetails = await getOpportunitiesDetails(apiPayload.email, systemDateTime);
        if (
          oppsBadDataDetails?.isBadDataAvailable &&
          alertDetails?.chipTitle !== 'Submitted' &&
          alertDetails?.chipTitle !== 'Resubmit'
        ) {
          alertDetails = {
            description: (
              <span>
                There are opportunities with fields that need to be corrected before submitting the
                forecast. These opportunities are highlighted in red below and require updates prior
                to submitting. For assistance, contact the{' '}
                <a
                  href="https://eyt.service-now.com/ey?id=sc_cat_item&table=sc_cat_item&sys_id=79ccbc5887c23d58ac3bfc86cebb355a&searchTerm=ey%20crm"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#477bbb', textDecoration: 'none' }}
                >
                  support team.
                </a>
              </span>
            ),
            title: ALERT_TITLE.NEEDS_ACTION,
            type: ALERT_TYPE.NEEDS_ACTION,
            chipTitle: alertDetails?.chipTitle,
            chipType: alertDetails?.chipType,
            chipVariant: alertDetails?.chipVariant,
            modifyStyle: null,
          };
        }
      }
    }
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Exiting getAlertBarDetails function with parameters ${JSON.stringify({
          quarterData,
          apiPayload,
          screenName,
          systemDateTime,
        })}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });
    return alertDetails;
  }
};

export const delay = ms =>
  new Promise(resolve => {
    return setTimeout(resolve, ms);
  });

export const retainCommitAndModifyOpportunity = (oldOpportunityData, newOpportunityData) => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering retainCommitAndModifyOpportunity function with parameters ${JSON.stringify(
        {
          oldOpportunityData,
          newOpportunityData,
        },
      )}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  return newOpportunityData.map(newItem => {
    const matchingItem = oldOpportunityData.find(
      oldItem => oldItem.opportunity_id === newItem.opportunity_id,
    );
    if (matchingItem) {
      newItem.commit_status = matchingItem.commit_status;

      return {
        ...newItem,
      };
    } else {
      return newItem;
    }
  });
};

export const isCurrentUserExist = async countryName => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering isCurrentUserExist function with parameters ${JSON.stringify({
        countryName,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  const previouslySelectedUser = JSON.parse(localStorage.getItem('selectedUser'));
  const accountIdToFind = previouslySelectedUser?.account_id;
  const userRoleDetails = await getUserRole(localStorage.getItem('userEmail'), countryName);
  const userRoleList = userRoleDetails?.roleList;
  let isAccountIdFound = false;

  userRoleList.forEach(role => {
    role.accountList.forEach(account => {
      if (account.account_id === accountIdToFind) {
        isAccountIdFound = true;
      }
    });
  });
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Exiting isCurrentUserExist function with parameters ${JSON.stringify({
        countryName,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  return isAccountIdFound;
};

export const isBadDataAvailable = data => {
  return data.some(item => item.is_baddata);
};

export const getCrmUpdateList = opportunitylist => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering getCrmUpdateList function with parameters ${JSON.stringify({
        opportunitylist,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  const crmUpdateList = opportunitylist
    .filter(item => item.commit_status !== item.last_submitted_status)
    .map(item => ({ opportunity_id: item.opportunity_id, commit_status: item.commit_status }));
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Exiting getCrmUpdateList function with parameters ${JSON.stringify({
        opportunitylist,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  return crmUpdateList;
};

export const sortAccountList = (firstAccount, secondAccount) => {
  // Change the priority, if new persona/ role is added
  // PL: 1
  // GCSP: 2
  // GCSP READ ONLY: 3
  // MSL: 4
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering sortAccountList function with parameters ${JSON.stringify({
        firstAccount,
        secondAccount,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  if (
    (firstAccount.apiRole === ROLE_CONST.GCSP && secondAccount.apiRole === ROLE_CONST.GCSP) ||
    (firstAccount.apiRole === ROLE_CONST.MSL && secondAccount.apiRole === ROLE_CONST.MSL) ||
    (firstAccount.apiRole === ROLE_CONST.GCSP_READ_ONLY &&
      secondAccount.apiRole === ROLE_CONST.GCSP_READ_ONLY)
  ) {
    const isFirstAccountNumber = /^\d/.test(firstAccount.name);
    const isSecondAccountNumber = /^\d/.test(secondAccount.name);

    if (isFirstAccountNumber && isSecondAccountNumber) {
      // Use 'name' instead of 'accountName' to avoid NaN from parseInt("GCSP: 3M", 10)
      return parseInt(firstAccount.name, 10) - parseInt(secondAccount.name, 10);
    } else if (isFirstAccountNumber) {
      return -1;
    } else if (isSecondAccountNumber) {
      return 1;
    } else {
      return firstAccount.accountName.localeCompare(secondAccount.accountName);
    }
  }
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering sortAccountList function with parameters ${firstAccount.priority} - ${secondAccount.priority}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  return firstAccount.priority - secondAccount.priority;
};

export const logError = (origin, error) => {
  const properties = {
    API_NAME: origin,
    SOURCE: SOURCE.FRONTEND,
    USERID: localStorage.getItem('userEmail'),
  };
  appInsights.trackException({ error: error, severityLevel: 3, properties });
};

export const getUserRoleList = (
  userAccountList,
  currentUser,
  allAccountsQuarterData,
  systemDateTime,
) => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: `Entering getUserRoleList function with parameters ${JSON.stringify({
        userAccountList,
        currentUser,
        allAccountsQuarterData,
        systemDateTime,
      })}`,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 1 represents response log
  });
  let modifiedAccountList = [];
  let alertStatusDetails;

  const onlyAccountList = userAccountList?.reduce((result, item) => {
    item.accountList.forEach(account => {
      result.push({ role: item.role, priority: item.priority, ...account });
    });
    return result;
  }, []);

  onlyAccountList?.forEach(account => {
    modifiedAccountList = [];

    const apiPayload = {
      email: account.role === ROLE_CONST.MSL_DELEGATE ? account?.parent_msl_id : currentUser.email,
      role: account.role,
      month:
        account.role !== ROLE_CONST.MSL_DELEGATE
          ? allAccountsQuarterData[account.role]?.month
          : allAccountsQuarterData['MSL']?.month,
      year:
        account.role !== ROLE_CONST.MSL_DELEGATE
          ? allAccountsQuarterData[account.role]?.fiscal_year
          : allAccountsQuarterData['MSL']?.fiscal_year,
      accountId: account.account_id,
    };

    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Calling getAlertBarDetails withparameters ${JSON.stringify({
          apiPayload,
        })}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });
    (async () => {
      try {
        alertStatusDetails = await getAlertBarDetails(
          account.role !== ROLE_CONST.MSL_DELEGATE
            ? allAccountsQuarterData[account.role]
            : allAccountsQuarterData['MSL'],
          apiPayload,
          null,
          systemDateTime,
        );

        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Response  from getAlertBarDetails ${JSON.stringify({
              alertStatusDetails,
            })}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 1, // 1 represents response log
        });

        if (apiPayload.role === 'PL') {
          // Added bcoz backend is not sending name: PL,
          // comming in API name: N.A.
          modifiedAccountList.push({
            ...account,
            role: account.role,
            apiRole: account.role,
            dueDetails: alertStatusDetails,
            accountName: 'Pursuit Leader',
          });
        } else if (apiPayload.role === 'GCSP') {
          modifiedAccountList.push({
            ...account,
            role: account.role,
            apiRole: account.role,
            dueDetails: alertStatusDetails,
            accountName: `GCSP: ${account.name}`,
          });
        } else if (apiPayload.role == 'GCSP READ ONLY') {
          modifiedAccountList.push({
            ...account,
            role: 'GCSP',
            apiRole: account.role,
            dueDetails: null,
            accountName: `Account Team: ${account.name}`,
          });
        } else if (apiPayload.role === 'MSL') {
          modifiedAccountList.push({
            role: account.role,
            apiRole: account.role,
            dueDetails: alertStatusDetails,
            accountName: `MSL: ${account.name}`,
            ...account,
          });
        } else if (apiPayload.role === ROLE_CONST.MSL_DELEGATE) {
          modifiedAccountList.push({
            ...account,
            role: 'MSL',
            apiRole: account.role,
            dueDetails: alertStatusDetails,
            accountName: `MSL-D: ${account.name}`,
          });
        } else if (apiPayload.role == 'MSL READ ONLY') {
          modifiedAccountList.push({
            ...account,
            role: 'MSL',
            apiRole: account.role,
            dueDetails: null,
            accountName: `MS/Sector Team: ${account.name}`,
          });
        } else {
          modifiedAccountList.push({
            role: account.role,
            apiRole: account.role,
            dueDetails: alertStatusDetails,
            accountName: account.name,
            ...account,
          });
        }
      } catch (error) {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Error from getAlertBarDetails ${JSON.stringify({
              error,
            })}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents error log
        });
        logError('getAlertBarDetails', error);
      }
    })();
  });
  appInsights.trackTrace({
    message: JSON.stringify({
      message: 'Exiting from getUserRoleList',
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 3 represents error log
  });
  return modifiedAccountList;
};

export const getCrmQuarterlyResponseData = (quarterOppsList, allOppsList) => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: 'Entering getCrmQuarterlyResponseData function with parameters',
      quarterOppsList,
      allOppsList,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 3 represents error log
  });
  if (quarterOppsList.length === 0) return quarterOppsList;

  const mergedData = quarterOppsList.map(item1 => {
    const match = allOppsList.find(item2 => item2.opportunity_id === item1.opportunity_id);
    return match ? { ...item1, ...match } : item1;
  });

  return mergedData;
};

export const getUpdatedCurrentRoleDueDetails = async (
  currentUser,
  quarterData,
  systemDateTime,
  flattenedAccountList,
) => {
  appInsights.trackTrace({
    message: JSON.stringify({
      message: 'Entering getCrmQuarterlyResponseData function with parameters',
      currentUser,
      quarterData,
      userId: localStorage.getItem('userEmail'),
      source: SOURCE.FRONTEND,
    }),
    severityLevel: 1, // 3 represents error log
  });
  const apiPayload = {
    email: currentUser.email,
    role: currentUser.role,
    month: quarterData.month.toLowerCase(),
    year: quarterData.fiscal_year,
    accountId: currentUser.account_id,
  };

  const updatedCurrentRoleDueDetails = await getAlertBarDetails(
    quarterData,
    apiPayload,
    null,
    systemDateTime,
  );

  const updatedflattenedAccountList = flattenedAccountList.map(item => {
    if (item.account_id === currentUser.account_id) {
      return {
        ...item,
        dueDetails: {
          // Update the dueDetails property
          ...updatedCurrentRoleDueDetails,
        },
      };
    }
    return item;
  });

  return updatedflattenedAccountList;
};

export const isMslDelegate = user => {
  return user?.parent_msl_id ? true : false;
};
